import React, { FC, useCallback, useEffect, useState } from "react";
import { Badge, Button, Spinner, Table } from "react-bootstrap";
import Swal from "sweetalert2";
import { AiGoalType } from "../../DTO";
import { AppUserApi } from "../../api";
import { useAppSelector } from "../../redux-config/hooks";
import { selectUser } from "../../redux-config/entities/user";
import { EditSuggestionsModal } from "./EditSuggestionsModal";

type Props = {
    userId: number;
};

const statusBadge = (status: AiGoalType["status"]) => {
    const map = {
        pending: "warning",
        approved: "success",
        rejected: "danger",
    } as const;
    return (
        <Badge bg={map[status]} className="text-capitalize">
            {status}
        </Badge>
    );
};

export const GoalApprovalSection: FC<Props> = ({ userId }) => {
    const [loading, setLoading] = useState(false);
    const [goals, setGoals] = useState<AiGoalType[]>([]);
    const [actionLoading, setActionLoading] = useState<number | null>(null);
    const [editGoal, setEditGoal] = useState<AiGoalType | null>(null);

    const user = useAppSelector(selectUser);

    const fetchGoals = useCallback(() => {
        if (!userId) return;
        setLoading(true);
        AppUserApi.GetUserGoals(user, userId)
            .then((data) => setGoals(data.goals))
            .catch((err) =>
                Swal.fire({ icon: "error", text: err }),
            )
            .finally(() => setLoading(false));
    }, [user, userId]);

    useEffect(() => {
        fetchGoals();
    }, [fetchGoals]);

    const handleApprove = (goalId: number) => {
        Swal.fire({
            title: "Approve this goal?",
            text: "The user will be able to see this goal in the app.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Approve",
            confirmButtonColor: "#198754",
        }).then((result) => {
            if (!result.isConfirmed) return;
            setActionLoading(goalId);
            AppUserApi.ApprovePendingGoal(user, goalId)
                .then(() => {
                    setGoals((prev) =>
                        prev.map((g) =>
                            g.id === goalId ? { ...g, status: "approved" } : g,
                        ),
                    );
                    Swal.fire({ icon: "success", title: "Goal approved", timer: 1500, showConfirmButton: false });
                })
                .catch((err) => Swal.fire({ icon: "error", text: err }))
                .finally(() => setActionLoading(null));
        });
    };

    const handleReject = (goalId: number) => {
        Swal.fire({
            title: "Reject this goal?",
            text: "The user will not see this goal.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Reject",
            confirmButtonColor: "#dc3545",
        }).then((result) => {
            if (!result.isConfirmed) return;
            setActionLoading(goalId);
            AppUserApi.RejectPendingGoal(user, goalId)
                .then(() => {
                    setGoals((prev) =>
                        prev.map((g) =>
                            g.id === goalId ? { ...g, status: "rejected" } : g,
                        ),
                    );
                    Swal.fire({ icon: "success", title: "Goal rejected", timer: 1500, showConfirmButton: false });
                })
                .catch((err) => Swal.fire({ icon: "error", text: err }))
                .finally(() => setActionLoading(null));
        });
    };

    const handleEditSaved = (updatedGoal: AiGoalType) => {
        setGoals((prev) => prev.map((g) => (g.id === updatedGoal.id ? updatedGoal : g)));
    };

    const handleEditSavedAndApproved = (goalId: number) => {
        setGoals((prev) => prev.map((g) => (g.id === goalId ? { ...g, status: "approved" } : g)));
    };

    return (
        <div className="mt-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0">AI Goals</h5>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={fetchGoals}
                    disabled={loading}
                >
                    {loading ? <Spinner animation="border" size="sm" /> : "Refresh"}
                </Button>
            </div>

            {loading && goals.length === 0 && (
                <div className="text-center py-4 text-muted">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading goals...
                </div>
            )}

            {!loading && goals.length === 0 && (
                <p className="text-muted">No goals generated yet for this user.</p>
            )}

            {goals.length > 0 && (
                <Table bordered hover responsive size="sm">
                    <thead className="table-light">
                        <tr>
                            <th>Type</th>
                            <th>Description</th>
                            <th>Focus Area</th>
                            <th>AI Used</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {goals.map((goal) => (
                            <tr key={goal.id}>
                                <td>
                                    <Badge bg={goal.type === "week" ? "primary" : "info"}>
                                        {goal.type === "week"
                                            ? `Week ${goal.weekId ?? ""}`
                                            : `Day ${goal.dayId ?? ""}`}
                                    </Badge>
                                </td>
                                <td style={{ maxWidth: 320 }}>
                                    <div
                                        className="text-truncate"
                                        style={{ maxWidth: 300 }}
                                        title={goal.description ?? ""}
                                    >
                                        {goal.description ?? "—"}
                                    </div>
                                    {goal.recommendedAction && (
                                        <small className="text-muted d-block">
                                            Action: {goal.recommendedAction}
                                        </small>
                                    )}
                                </td>
                                <td>{goal.focusArea ?? "—"}</td>
                                <td>
                                    <Badge bg={goal.aiUsed ? "success" : "secondary"}>
                                        {goal.aiUsed ? "AI" : "Fallback"}
                                    </Badge>
                                </td>
                                <td>{statusBadge(goal.status)}</td>
                                <td>
                                    <div className="d-flex gap-1 flex-wrap">
                                        {goal.type === "week" && goal.status === "pending" && (
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                disabled={actionLoading === goal.id}
                                                onClick={() => setEditGoal(goal)}
                                            >
                                                Edit
                                            </Button>
                                        )}
                                        <Button
                                            size="sm"
                                            variant="success"
                                            disabled={
                                                goal.status !== "pending" ||
                                                actionLoading === goal.id
                                            }
                                            onClick={() => handleApprove(goal.id)}
                                        >
                                            {actionLoading === goal.id ? (
                                                <Spinner animation="border" size="sm" />
                                            ) : (
                                                "Approve"
                                            )}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="danger"
                                            disabled={
                                                goal.status !== "pending" ||
                                                actionLoading === goal.id
                                            }
                                            onClick={() => handleReject(goal.id)}
                                        >
                                            Reject
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            {editGoal && (
                <EditSuggestionsModal
                    goal={editGoal}
                    show={!!editGoal}
                    onHide={() => setEditGoal(null)}
                    onSaved={handleEditSaved}
                    onSavedAndApproved={handleEditSavedAndApproved}
                />
            )}
        </div>
    );
};
